import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { useMemo, useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { EquipmentManagementTask } from "../../types/api";
import { differenceInMonths, parseISO, format } from "date-fns";
import { RefreshCw } from "lucide-react";
import { ColDef } from "ag-grid-community";
import Modal from "../../components/modal/Modal";
import RemediationActionForm from "./RemediationActionForm";
import { SpinLoader } from "../../components/loader/Loader";
import images from "../../assets/images";
import { useAuth } from "../../auth/AuthProvider";
import {
  useEquipmentManagementStore,
  useOrgUsersStore,
} from "../../store/store";

// Status color mapping
const STATUS_COLORS: Record<string, string> = {
  Completed: "text-[#4CAF50]",
  "In Progress": "text-[#2196F3]",
  Scheduled: "text-[#FF9800]",
};

// Common cell style
const DEFAULT_CELL_STYLE = {
  padding: "10px 10px 0 10px",
};

const EquipmentTasks = () => {
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEquipment, setSelectedEquipment] =
    useState<EquipmentManagementTask | null>(null);
  const [mode, setMode] = useState<"" | "update" | "view">("");
  const [saving, setSaving] = useState(false);

  const { user } = useAuth();
  const userRole = user?.roles?.[0];
  const { getAllEquipmentManagementTaskHandler, equipmentManagementTasks } =
    useEquipmentManagementStore();
  const { orgUsersData, fetchOrgUsers } = useOrgUsersStore();

  const userMap = useMemo(() => {
    const map = new Map<string, { firstName: string; lastName: string }>();
    orgUsersData.forEach((user: any) => {
      if (user._id) {
        map.set(user._id, {
          firstName: user.firstName || "N/A",
          lastName: user.lastName || "N/A",
        });
      }
    });
    return map;
  }, [orgUsersData]);

  useEffect(() => {
    fetchOrgUsers();
  }, [fetchOrgUsers]);

  // Process and filter data in a single memoized computation
  const filteredData = useMemo(() => {
    if (!equipmentManagementTasks || equipmentManagementTasks.length === 0) {
      return [];
    }

    // First, filter by user role if needed
    let tasks = equipmentManagementTasks;
    if (userRole !== "orgadmin" && user?.id) {
      tasks = equipmentManagementTasks.filter(
        (task: EquipmentManagementTask) => task.responsiblePerson === user.id,
      );
    }

    // Then, enrich with user names for display
    return tasks.map((task: EquipmentManagementTask) => {
      const userData = userMap.get(task.responsiblePerson);
      const responsiblePersonName = userData
        ? `${userData.firstName} ${userData.lastName}`
        : "N/A";

      return {
        ...task,
        responsiblePersonName, // Keep original responsiblePerson (ID) for reference
      };
    });
  }, [equipmentManagementTasks, userMap, userRole, user?.id]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await getAllEquipmentManagementTaskHandler();
      } catch (error: any) {
        toast.error("Failed to fetch equipment data");
        console.error("Error fetching equipment data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [getAllEquipmentManagementTaskHandler]);

  const handleRefresh = useCallback(async () => {
    try {
      setLoading(true);
      await getAllEquipmentManagementTaskHandler();
      toast.success("Data refreshed successfully");
    } catch (error: any) {
      toast.error("Failed to refresh data");
      console.error("Error refreshing data:", error);
    } finally {
      setLoading(false);
    }
  }, [getAllEquipmentManagementTaskHandler]);

  const handleEditAction = useCallback((equipment: EquipmentManagementTask) => {
    setSelectedEquipment(equipment);
    setIsModalOpen(true);
    setMode("update");
  }, []);

  const handleViewAction = useCallback((equipment: EquipmentManagementTask) => {
    setSelectedEquipment(equipment);
    setMode("view");
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    if (!saving) {
      setIsModalOpen(false);
      setSelectedEquipment(null);
      setMode("");
    }
  }, [saving]);

  // Format warranty expiry date
  const formatWarrantyDate = useCallback((value: string) => {
    if (!value) return "";
    try {
      return format(parseISO(value), "MMM dd, yyyy");
    } catch {
      return value;
    }
  }, []);

  // Calculate months until expiry
  const getMonthsUntilExpiry = useCallback((expiryDate: string) => {
    if (!expiryDate) return "N/A";
    try {
      const expiry = parseISO(expiryDate);
      const now = new Date();
      const months = differenceInMonths(expiry, now);
      return `${months} months`;
    } catch {
      return "Invalid date";
    }
  }, []);

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Equipment ID",
        field: "equipmentId",
        width: 120,
        cellStyle: DEFAULT_CELL_STYLE,
      },
      {
        headerName: "Equipment Name",
        field: "equipmentName",
        width: 180,
        cellStyle: DEFAULT_CELL_STYLE,
      },
      {
        headerName: "Serial Number",
        field: "serialNumber",
        width: 150,
        cellStyle: DEFAULT_CELL_STYLE,
      },
      {
        headerName: "Warranty Expiry",
        field: "warrantyExpiryDate",
        valueFormatter: (params: any) => formatWarrantyDate(params.value),
        cellStyle: DEFAULT_CELL_STYLE,
        width: 150,
      },
      {
        headerName: "Months Until Expiry",
        valueGetter: (params: any) =>
          getMonthsUntilExpiry(params.data.warrantyExpiryDate),
        cellStyle: DEFAULT_CELL_STYLE,
        width: 150,
      },
      {
        headerName: "Remediation Action",
        field: "remediationAction",
        cellRenderer: (params: any) => {
          const action = params.data?.remediationAction || "";
          return <div>{action || "No action recorded"}</div>;
        },
        cellStyle: DEFAULT_CELL_STYLE,
        width: 200,
      },
      {
        headerName: "Responsible Person",
        field: "responsiblePersonName",
        cellStyle: DEFAULT_CELL_STYLE,
        width: 180,
      },
      {
        headerName: "Status",
        field: "status",
        width: 150,
        cellRenderer: (params: any) => {
          const status = params.data?.status || "N/A";
          const statusColor =
            STATUS_COLORS[status] || "text-gray-500 font-bold";
          return <div className={`font-bold ${statusColor}`}>{status}</div>;
        },
        cellStyle: DEFAULT_CELL_STYLE,
      },
      {
        headerName: "Actions",
        cellStyle: DEFAULT_CELL_STYLE,
        cellRenderer: (params: any) => {
          const equipment = params.data;
          return (
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleEditAction(equipment)}
                className="mt-2 iconTd"
                title="Edit Action"
                aria-label="Edit Action"
              >
                <img src={images.editIcon} alt="edit" />
              </button>
              <button
                onClick={() => handleViewAction(equipment)}
                className="mt-2 iconTd"
                title="View Action"
                aria-label="View Action"
              >
                <img src={images.viewIcon} alt="view" />
              </button>
            </div>
          );
        },
        width: 100,
      },
    ],
    [
      handleEditAction,
      handleViewAction,
      formatWarrantyDate,
      getMonthsUntilExpiry,
    ],
  );
  return (
    <div className="px-2 py-4 mt-14  min-h-100vh">
      <div className="flex justify-between items-center">
        <h2 className="mb-3">Tasks</h2>
        <button
          className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full hover:bg-[#0d8f8c] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleRefresh}
          disabled={loading}
          aria-label="Refresh data"
        >
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <div className="ag-theme-alpine mt-2 bg-white rounded shadow">
        {loading ? (
          <div className="text-center py-8">
            <div className="text-lg">Loading warranty tasks...</div>
          </div>
        ) : !filteredData || filteredData.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-lg">
              No equipment with warranties expiring in 3-6 months
            </div>
            <p className="text-gray-600 mt-2">
              Equipment will appear here when their warranty is within the
              expiry window.
            </p>
          </div>
        ) : (
          <AgGridReact
            columnDefs={columnDefs}
            rowData={filteredData}
            rowHeight={60}
            headerHeight={65}
            suppressCellFocus={true}
            animateRows={true}
            domLayout="autoHeight"
            pagination={true}
            paginationPageSize={10}
            paginationPageSizeSelector={[5, 10, 20]}
            defaultColDef={{
              resizable: true,
            }}
          />
        )}
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        closeButton={false}
        modalClass="bg-white w-full max-w-3xl rounded-xl shadow-lg max-h-[80vh] flex flex-col relative"
      >
        {selectedEquipment && (
          <div className="relative">
            {saving && (
              <div className="absolute inset-0 bg-white bg-opacity-80 flex items-center justify-center z-10 rounded-xl">
                <div className="text-center">
                  <SpinLoader />
                  <p className="mt-3 text-gray-600">
                    Saving action and sending notification...
                  </p>
                </div>
              </div>
            )}

            <RemediationActionForm
              equipmentId={selectedEquipment._id!}
              initialData={selectedEquipment}
              warrantyExpiryDate={selectedEquipment.warrantyExpiryDate}
              onCancel={handleCloseModal}
              mode={mode}
              disabled={saving}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EquipmentTasks;
